import { useEffect, useState } from 'react';
import { Button } from '@/design-system';
import { navigate } from '@/app/router';
import type { FunnelDetail } from '@/contracts/funnel';
import { useAuthStore } from '@/features/auth/state/auth-store';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import { FUNNEL_SCHEMA_VERSION } from '@/domain/funnel/schema-version';
import { normalizeEditorGraph } from '../domain/normalize-graph';
import { useEditorStore } from '../state/editor-store';
import { createRecoveryStore } from '../persistence/recovery/indexeddb-recovery-store';
import type { RecoveryRecord } from '../persistence/recovery/recovery-store';
import { EditorErrorBoundary } from './EditorErrorBoundary';
import { EditorShell } from './EditorShell';

type LoadState =
  | { status: 'loading' }
  | { status: 'recovery-prompt'; funnel: FunnelDetail; recovery: RecoveryRecord }
  | { status: 'ready'; funnel: FunnelDetail }
  | { status: 'error'; message: string };

/**
 * SPEC-007 §21/plan §10 — recovery is detected on load and NEVER applied
 * silently (SAVE-REQ-019): a schema-incompatible record (from a newer
 * client) is blocked from recovery entirely (SAVE-REQ-020), everything
 * else is an explicit Recover/Discard choice.
 */
export function EditorPage({ id }: { id: string }) {
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const initialize = useEditorStore((state) => state.initialize);
  const markRecovered = useEditorStore((state) => state.markRecovered);
  const userId = useAuthStore((state) => state.user?.id);

  useEffect(() => {
    let active = true;
    setLoadState({ status: 'loading' });
    (async () => {
      try {
        const { funnel } = await dashboardApi.getFunnel(id);
        if (!active) return;
        if (userId) {
          const { store: recoveryStore } = createRecoveryStore();
          const recovery = await recoveryStore.get(userId, id);
          if (!active) return;
          if (recovery) {
            setLoadState({ status: 'recovery-prompt', funnel, recovery });
            return;
          }
        }
        initialize(normalizeEditorGraph(funnel.graph));
        setLoadState({ status: 'ready', funnel });
      } catch (error) {
        if (active) {
          setLoadState({
            status: 'error',
            message: error instanceof Error ? error.message : 'O grafo do funil é inválido.',
          });
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [id, initialize, userId]);

  if (loadState.status === 'loading') {
    return (
      <main
        role="status"
        className="flex h-[calc(100vh-3.5rem)] items-center justify-center text-sm text-text-muted"
      >
        Carregando editor…
      </main>
    );
  }
  if (loadState.status === 'error') {
    return (
      <main
        role="alert"
        className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center"
      >
        <h1 className="text-xl font-semibold text-text-primary">Não foi possível abrir este funil</h1>
        <p className="max-w-md text-sm text-text-secondary">{loadState.message}</p>
        <Button variant="secondary" onClick={() => navigate('/app')}>
          Voltar ao painel
        </Button>
      </main>
    );
  }
  if (loadState.status === 'recovery-prompt') {
    const { funnel, recovery } = loadState;
    const schemaIncompatible = recovery.schemaVersion > FUNNEL_SCHEMA_VERSION;
    const diverged = recovery.baseRevision !== funnel.revision;
    const { store: recoveryStore } = createRecoveryStore();

    const discard = async () => {
      if (userId) await recoveryStore.remove(userId, id);
      try {
        initialize(normalizeEditorGraph(funnel.graph));
        setLoadState({ status: 'ready', funnel });
      } catch (error) {
        setLoadState({
          status: 'error',
          message: error instanceof Error ? error.message : 'O grafo do funil é inválido.',
        });
      }
    };
    const recover = () => {
      try {
        initialize(normalizeEditorGraph(recovery.graph));
        markRecovered();
        setLoadState({ status: 'ready', funnel });
      } catch (error) {
        setLoadState({
          status: 'error',
          message: error instanceof Error ? error.message : 'O grafo recuperado é inválido.',
        });
      }
    };

    return (
      <main
        role="alertdialog"
        aria-label="Alterações locais não salvas encontradas"
        className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center"
      >
        <h1 className="text-xl font-semibold text-text-primary">Alterações locais não salvas encontradas</h1>
        <p className="max-w-md text-sm text-text-secondary">
          {schemaIncompatible
            ? 'Estas alterações locais foram feitas com uma versão mais nova do editor e não podem ser recuperadas aqui.'
            : diverged
              ? 'Este funil foi alterado em outro lugar desde que estas alterações locais foram feitas. Você pode recuperá-las como uma nova edição sobre a versão mais recente ou descartá-las.'
              : 'Encontramos alterações salvas localmente para este funil que nunca foram confirmadas como salvas. Recupere-as ou descarte-as e abra a última versão salva.'}
        </p>
        <p className="text-xs text-text-muted">
          Salvo localmente em {new Date(recovery.savedLocallyAt).toLocaleString('pt-BR')}
        </p>
        <div className="flex gap-3">
          {!schemaIncompatible ? (
            <Button variant="primary" onClick={recover}>
              Recuperar alterações locais
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => void discard()}>
            Descartar alterações locais
          </Button>
        </div>
      </main>
    );
  }
  return (
    <EditorErrorBoundary>
      <EditorShell
        id={id}
        name={loadState.funnel.name}
        description={loadState.funnel.description}
        revision={loadState.funnel.revision}
      />
    </EditorErrorBoundary>
  );
}
